import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styles from './SignUp.module.scss';
import { UnifiedLayout } from '../../layouts/UnifiedLayout/UnifiedLayout';
import { Divider, Form, Header } from '@solarwindscloud/nighthawk-ui';
import {
  DocumentTitle,
  ErrorMessage,
  EmailInput,
  PasswordInput,
  FirstNameInput,
  LastNameInput,
  OrgInput,
  SubmitButton,
} from '../../components';

const FORM_STATE = {
  INITIAL: 'INITIAL',
  LOADING: 'LOADING',
};

const initialState = {
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  organization: '',
};

const SignUp = ({ email: emailFromProps, invitationToken, redirectTo, signUp }) => {
  const [state, setState] = useState({ ...initialState, email: emailFromProps || '' });
  const [errors, setErrors] = useState({ ...initialState, signUpError: '' });

  const [formState, setFormState] = useState(FORM_STATE.INITIAL);

  const handleChange = useCallback(
    prop => value => {
      setState({ ...state, [prop]: value });
      setErrors({ ...errors, [prop]: '', signUp: '' });
    },
    [state, errors]
  );

  const handleError = useCallback(prop => error => setErrors({ ...errors, [prop]: error }), [errors]);

  const hasFormError = () => Object.values(errors).some(value => !!value);

  const isFormValid = () =>
    !Object.keys(state).some(
      key => !state[key] && (key !== 'organization' || (key === 'organization' && !invitationToken))
    );

  const handleSubmit = useCallback(async () => {
    try {
      setFormState(FORM_STATE.LOADING);
      const result = await signUp({ ...state, invitationToken: invitationToken });
      redirectTo(result.redirectUrl);
    } catch (e) {
      setFormState(FORM_STATE.INITIAL);
      setErrors({ ...errors, signUp: e.message });
    }
  }, [redirectTo, errors, state, invitationToken, signUp]);

  const renderCredentialsInputs = () => (
    <>
      <EmailInput
        value={state.email}
        onChange={handleChange('email')}
        onError={handleError('email')}
        hasError={!!errors.email}
        disabled={!!invitationToken}
        errorMessage="Invalid email address. Please correct this field"
        required
      />
      <PasswordInput
        value={state.password}
        hasError={!!errors.password}
        onChange={handleChange('password')}
        onError={handleError('password')}
        required
      />
    </>
  );

  const renderNamesInputs = () => (
    <>
      <FirstNameInput
        value={state.firstName}
        hasError={!!errors.firstName}
        onError={handleError('firstName')}
        onChange={handleChange('firstName')}
        required
      />
      <LastNameInput
        value={state.lastName}
        hasError={!!errors.lastName}
        onChange={handleChange('lastName')}
        onError={handleError('lastName')}
        required
      />
      {!invitationToken && (
        <OrgInput
          value={state.organization}
          hasError={!!errors.organization}
          onChange={handleChange('organization')}
          onError={handleError('organization')}
          required
        />
      )}
    </>
  );

  const renderForm = () => (
    <Form className={styles.form} onSubmit={handleSubmit} error={hasFormError()}>
      {!!errors.signUp && <ErrorMessage message={errors.signUp} />}
      {renderCredentialsInputs()}
      {renderNamesInputs()}
      <Divider hidden section />
      <SubmitButton primary loading={formState === FORM_STATE.LOADING} disabled={!isFormValid() || hasFormError()}>
        Create account
      </SubmitButton>
    </Form>
  );

  const introduction = !!invitationToken
    ? 'Create your SolarWinds profile to continue.'
    : 'We need some details to create your user account.';
  return (
    <DocumentTitle title="User Sign Up/Registration">
      <UnifiedLayout headerText="User Sign Up/Registration" dataTestId="intro-text">
        <Header textAlign="center">
          <Header.Subheader>{introduction}</Header.Subheader>
        </Header>
        {renderForm()}
      </UnifiedLayout>
    </DocumentTitle>
  );
};

SignUp.propTypes = {
  email: PropTypes.string,
  invitationToken: PropTypes.string,
  redirectTo: PropTypes.func.isRequired,
  signUp: PropTypes.func.isRequired,
};

export default SignUp;
